import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";
import NumberFormatCustom from "../../globals/utils/Bond Creation Utils/numberFormatCustom";
import {
  DWB_LABEL_MAP,
  isNameChangeEndorsement,
} from "../Law Firm User/helpers/lawFirmBondsHelpers";
import {
  getEndorsementInstructions as defaultEndorsementInstructions,
} from "../Law Firm User/helpers/lawFirmBondsCancellationCopy";
import { bondEndorsementDialogPropTypes } from "./bondEndorsementDialogPropTypes";
import EndorsementCountyField from "./endorsement/EndorsementCountyField";
import EndorsementDwbNameFields from "./endorsement/EndorsementDwbNameFields";
import EndorsementPrincipalPickerFields from "./endorsement/EndorsementPrincipalPickerFields";
import { getPrincipalPickerConfig } from "./endorsement/endorsementPrincipalPickerConfig";

const VALID_DOC_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

function defaultFileChangeHandler(setEndorsementFile) {
  return (e) => {
    const file = e.target.files[0];
    if (file && !VALID_DOC_TYPES.has(file.type)) {
      toast.error("Only PDF or Word documents are allowed.");
      e.target.value = "";
      return;
    }
    setEndorsementFile(file);
    toast.success("File selected successfully!");
  };
}

function computeArchivesSubmitDisabled({
  isSubmittingEndorsement,
  endorsementType,
  endorsementFile,
  endorsementToCounty,
  selectedBondToEndorse,
  updatedName,
  dwbFirstName,
  dwbLastName,
}) {
  return (
    isSubmittingEndorsement ||
    (!isNameChangeEndorsement(endorsementType) && !endorsementFile) ||
    (endorsementType === "Name_Change_County" && !endorsementToCounty) ||
    (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" &&
      !(DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single
        ? updatedName
        : dwbFirstName || dwbLastName))
  );
}

function resolveOnClose(onCloseProp, setEndorsementDialogOpen, isSubmittingEndorsement) {
  return (
    onCloseProp ??
    (setEndorsementDialogOpen
      ? () => !isSubmittingEndorsement && setEndorsementDialogOpen(false)
      : undefined)
  );
}

function resolveOnFileChange(handleEndorsementSupportingFileChange, setEndorsementFile) {
  return (
    handleEndorsementSupportingFileChange ??
    (setEndorsementFile ? defaultFileChangeHandler(setEndorsementFile) : undefined)
  );
}

function resolveInternalDisabled({
  variant,
  internalReleaseDisabled,
  selectedBondToEndorse,
  isSubmittingEndorsement,
}) {
  return variant === "lawFirm"
    ? internalReleaseDisabled
    : !selectedBondToEndorse || isSubmittingEndorsement;
}

function resolveLawFirmSubmitDisabled({
  variant,
  lawFirmEndorseSubmitDisabled,
  archivesSubmitDisabled,
}) {
  return variant === "lawFirm" ? lawFirmEndorseSubmitDisabled : archivesSubmitDisabled;
}

function EndorsementTypeSpecificField({
  endorsementType,
  counties,
  loadingCounties,
  endorsementToCounty,
  setEndorsementToCounty,
  selectedBondToEndorse,
  updatedName,
  setUpdatedName,
  dwbFirstName,
  setDwbFirstName,
  dwbMiddleName,
  setDwbMiddleName,
  dwbLastName,
  setDwbLastName,
  dwbSuffix,
  setDwbSuffix,
  dwbAlias,
  setDwbAlias,
  endorsementPrincipalId,
  setEndorsementPrincipalId,
  newBondAmount,
  setNewBondAmount,
}) {
  if (!isNameChangeEndorsement(endorsementType)) {
    return (
      <TextField
        label="New Bond Amount (Rounded Up)"
        name="newBondAmount"
        value={newBondAmount}
        onChange={(e) => setNewBondAmount(e.target.value)}
        fullWidth
        required
        slotProps={{ input: {
          inputComponent: NumberFormatCustom,
        }}}
        sx={{ mb: 3 }}
      />
    );
  }

  if (endorsementType === "Name_Change_County") {
    return (
      <EndorsementCountyField
        counties={counties}
        loadingCounties={loadingCounties}
        endorsementToCounty={endorsementToCounty}
        setEndorsementToCounty={setEndorsementToCounty}
      />
    );
  }

  if (
    endorsementType === "Name_Change_Principal" &&
    getPrincipalPickerConfig(selectedBondToEndorse?.bond_type) &&
    setEndorsementPrincipalId
  ) {
    return (
      <EndorsementPrincipalPickerFields
        selectedBondToEndorse={selectedBondToEndorse}
        updatedName={updatedName}
        setUpdatedName={setUpdatedName}
        endorsementPrincipalId={endorsementPrincipalId}
        setEndorsementPrincipalId={setEndorsementPrincipalId}
      />
    );
  }

  if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
    return (
      <EndorsementDwbNameFields
        selectedBondToEndorse={selectedBondToEndorse}
        updatedName={updatedName}
        setUpdatedName={setUpdatedName}
        dwbFirstName={dwbFirstName}
        setDwbFirstName={setDwbFirstName}
        dwbMiddleName={dwbMiddleName}
        setDwbMiddleName={setDwbMiddleName}
        dwbLastName={dwbLastName}
        setDwbLastName={setDwbLastName}
        dwbSuffix={dwbSuffix}
        setDwbSuffix={setDwbSuffix}
        dwbAlias={dwbAlias}
        setDwbAlias={setDwbAlias}
      />
    );
  }

  return (
    <TextField
      label="Updated Name"
      name="updatedName"
      value={updatedName}
      onChange={(e) => setUpdatedName(e.target.value)}
      fullWidth
      required
      sx={{ mb: 3 }}
    />
  );
}

function EstimatedAssetsField({
  selectedBondToEndorse,
  estimatedAssets,
  setEstimatedAssets,
}) {
  if (
    selectedBondToEndorse.bond_type !== "Probate" &&
    selectedBondToEndorse.bond_type !== "Guardianship"
  ) {
    return null;
  }

  return (
    <TextField
      label="Updated Estimated Assets (optional)"
      type="text"
      inputMode="decimal"
      value={estimatedAssets}
      onChange={(e) => setEstimatedAssets(e.target.value)}
      fullWidth
      placeholder="e.g. 250000"
      sx={{ mb: 2 }}
      helperText="Fill the updated estimated assets value if additional assets were discovered."
    />
  );
}

function EndorsementFormContent({
  selectedBondToEndorse,
  endorsementType,
  setEndorsementType,
  newBondAmount,
  setNewBondAmount,
  counties,
  endorsementToCounty,
  setEndorsementToCounty,
  loadingCounties,
  estimatedAssets,
  setEstimatedAssets,
  updatedName,
  setUpdatedName,
  dwbFirstName,
  setDwbFirstName,
  dwbMiddleName,
  setDwbMiddleName,
  dwbLastName,
  setDwbLastName,
  dwbSuffix,
  setDwbSuffix,
  dwbAlias,
  setDwbAlias,
  endorsementPrincipalId,
  setEndorsementPrincipalId,
  resolveInstructions,
  isSubmittingEndorsement,
  endorsementFile,
  onFileChange,
  contactName,
  setContactName,
  contactEmail,
  setContactEmail,
}) {
  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Endorsement Details
      </Typography>
      <Typography sx={{ mb: 1 }}>
        <strong>Bond Number:</strong>{" "}
        {selectedBondToEndorse.bond_number ?? "N/A"}
      </Typography>
      <Typography sx={{ mb: 2 }}>
        <strong>Bond Type:</strong> {selectedBondToEndorse.bond_type}
      </Typography>

      <TextField
        label="Endorsement Type"
        select
        fullWidth
        value={endorsementType}
        onChange={(e) => setEndorsementType(e.target.value)}
        slotProps={{ select: { native: true }}}
        required
        sx={{ mb: 2 }}
      >
        <option value="Increase">Increase</option>
        <option value="Decrease">Decrease</option>
        <option value="Name_Change_Principal">Name change (Principal)</option>
        <option value="Name_Change_County">Name change (County)</option>
        <option value="Name_Change_Deceased_Ward_Beneficiary">
          Name change (Deceased/Ward/Beneficiary)
        </option>
      </TextField>

      <EndorsementTypeSpecificField
        endorsementType={endorsementType}
        counties={counties}
        loadingCounties={loadingCounties}
        endorsementToCounty={endorsementToCounty}
        setEndorsementToCounty={setEndorsementToCounty}
        selectedBondToEndorse={selectedBondToEndorse}
        updatedName={updatedName}
        setUpdatedName={setUpdatedName}
        dwbFirstName={dwbFirstName}
        setDwbFirstName={setDwbFirstName}
        dwbMiddleName={dwbMiddleName}
        setDwbMiddleName={setDwbMiddleName}
        dwbLastName={dwbLastName}
        setDwbLastName={setDwbLastName}
        dwbSuffix={dwbSuffix}
        setDwbSuffix={setDwbSuffix}
        dwbAlias={dwbAlias}
        setDwbAlias={setDwbAlias}
        endorsementPrincipalId={endorsementPrincipalId}
        setEndorsementPrincipalId={setEndorsementPrincipalId}
        newBondAmount={newBondAmount}
        setNewBondAmount={setNewBondAmount}
      />

      <EstimatedAssetsField
        selectedBondToEndorse={selectedBondToEndorse}
        estimatedAssets={estimatedAssets}
        setEstimatedAssets={setEstimatedAssets}
      />

      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Documents
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
        Acceptable Documentation:
      </Typography>
      <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
        {resolveInstructions(selectedBondToEndorse).map((item, idx) => (
          <li
            key={`endorse-doc-${idx}-${item}`}
            style={{ fontSize: "0.9rem", marginBottom: "6px" }}
          >
            {item}
          </li>
        ))}
      </ul>

      <Button
        variant="outlined"
        component="label"
        disabled={isSubmittingEndorsement}
        startIcon={<CloudUploadIcon />}
        sx={{ mb: 2 }}
      >
        Upload Supporting Document{" "}
        <input
          type="file"
          hidden
          accept=".pdf,.doc,.docx"
          onChange={onFileChange}
        />
      </Button>
      {endorsementFile && (
        <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
          Selected File: {endorsementFile.name}
        </Typography>
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.5 }}
      >
        Allowed file types: PDF, DOC, DOCX
      </Typography>

      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Contact Information
      </Typography>
      <TextField
        label=" Name"
        name="contactName"
        value={contactName}
        onChange={(e) => setContactName(e.target.value)}
        fullWidth
        required
        placeholder="Full name"
        sx={{ mb: 2 }}
      />

      <TextField
        label=" Email"
        type="email"
        name="contactEmail"
        value={contactEmail}
        onChange={(e) => setContactEmail(e.target.value)}
        fullWidth
        required
        placeholder="name@example.com"
        sx={{ mb: 1 }}
      />
    </>
  );
}

function EndorsementDialogActionButtons({
  selectedBondToEndorse,
  role,
  internalDisabled,
  releaseHandler,
  lawFirmSubmitDisabled,
  submitHandler,
  isSubmittingEndorsement,
}) {
  if (!selectedBondToEndorse || selectedBondToEndorse.bond_type === "Blanket") {
    return null;
  }

  const isInternal = role === "internal_user" || role === "super_user";

  return (
    <>
      {isInternal && (
        <Button
          variant="outlined"
          color="secondary"
          disabled={internalDisabled}
          onClick={releaseHandler}
        >
          Release Rider
        </Button>
      )}
      {!isInternal && (
        <Button
          variant="contained"
          disabled={lawFirmSubmitDisabled}
          onClick={submitHandler}
        >
          {isSubmittingEndorsement ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Submit Endorsement"
          )}
        </Button>
      )}
    </>
  );
}

export default function BondEndorsementDialog({
  variant = "lawFirm",
  open: openProp,
  onClose: onCloseProp,
  endorsementDialogOpen,
  setEndorsementDialogOpen,
  selectedBondToEndorse,
  endorsementType,
  setEndorsementType,
  newBondAmount,
  setNewBondAmount,
  endorsementFile,
  setEndorsementFile,
  isSubmittingEndorsement,
  contactName,
  setContactName,
  contactEmail,
  setContactEmail,
  updatedName,
  setUpdatedName,
  counties,
  endorsementToCounty,
  setEndorsementToCounty,
  loadingCounties,
  estimatedAssets,
  setEstimatedAssets,
  dwbFirstName,
  setDwbFirstName,
  dwbMiddleName,
  setDwbMiddleName,
  dwbLastName,
  setDwbLastName,
  dwbSuffix,
  setDwbSuffix,
  dwbAlias,
  setDwbAlias,
  endorsementPrincipalId,
  setEndorsementPrincipalId,
  role,
  nameChangeOk,
  internalReleaseDisabled,
  lawFirmEndorseSubmitDisabled,
  onReleaseRider,
  onSubmitEndorsement,
  handleReleaseRider,
  handleSubmitEndorsementRequest,
  handleEndorsementSupportingFileChange,
  getEndorsementInstructions: getEndorsementInstructionsProp,
}) {
  const open =
    openProp ?? endorsementDialogOpen ?? false;
  const onClose = resolveOnClose(
    onCloseProp,
    setEndorsementDialogOpen,
    isSubmittingEndorsement
  );

  const resolveInstructions =
    getEndorsementInstructionsProp ?? defaultEndorsementInstructions;

  const onFileChange = resolveOnFileChange(
    handleEndorsementSupportingFileChange,
    setEndorsementFile
  );

  const releaseHandler = onReleaseRider ?? handleReleaseRider;
  const submitHandler = onSubmitEndorsement ?? handleSubmitEndorsementRequest;

  const archivesSubmitDisabled =
    variant !== "lawFirm" &&
    computeArchivesSubmitDisabled({
      isSubmittingEndorsement,
      endorsementType,
      endorsementFile,
      endorsementToCounty,
      selectedBondToEndorse,
      updatedName,
      dwbFirstName,
      dwbLastName,
    });

  const internalDisabled = resolveInternalDisabled({
    variant,
    internalReleaseDisabled,
    selectedBondToEndorse,
    isSubmittingEndorsement,
  });

  const lawFirmSubmitDisabled = resolveLawFirmSubmitDisabled({
    variant,
    lawFirmEndorseSubmitDisabled,
    archivesSubmitDisabled,
  });

  let dialogContent;
  if (selectedBondToEndorse?.bond_type === "Blanket") {
    dialogContent = (
      <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
        Endorsement not allowed for Blanket.
      </Typography>
    );
  } else if (selectedBondToEndorse) {
    dialogContent = (
      <EndorsementFormContent
        selectedBondToEndorse={selectedBondToEndorse}
        endorsementType={endorsementType}
        setEndorsementType={setEndorsementType}
        newBondAmount={newBondAmount}
        setNewBondAmount={setNewBondAmount}
        counties={counties}
        endorsementToCounty={endorsementToCounty}
        setEndorsementToCounty={setEndorsementToCounty}
        loadingCounties={loadingCounties}
        estimatedAssets={estimatedAssets}
        setEstimatedAssets={setEstimatedAssets}
        updatedName={updatedName}
        setUpdatedName={setUpdatedName}
        dwbFirstName={dwbFirstName}
        setDwbFirstName={setDwbFirstName}
        dwbMiddleName={dwbMiddleName}
        setDwbMiddleName={setDwbMiddleName}
        dwbLastName={dwbLastName}
        setDwbLastName={setDwbLastName}
        dwbSuffix={dwbSuffix}
        setDwbSuffix={setDwbSuffix}
        dwbAlias={dwbAlias}
        setDwbAlias={setDwbAlias}
        endorsementPrincipalId={endorsementPrincipalId}
        setEndorsementPrincipalId={setEndorsementPrincipalId}
        resolveInstructions={resolveInstructions}
        isSubmittingEndorsement={isSubmittingEndorsement}
        endorsementFile={endorsementFile}
        onFileChange={onFileChange}
        contactName={contactName}
        setContactName={setContactName}
        contactEmail={contactEmail}
        setContactEmail={setContactEmail}
      />
    );
  } else {
    dialogContent = (
      <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
        No bond selected.
      </Typography>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1.3rem" }}>
        Bond Endorsement
      </DialogTitle>
      <DialogContent dividers>
        {dialogContent}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmittingEndorsement}>
          Close
        </Button>
        <EndorsementDialogActionButtons
          selectedBondToEndorse={selectedBondToEndorse}
          role={role}
          internalDisabled={internalDisabled}
          releaseHandler={releaseHandler}
          lawFirmSubmitDisabled={lawFirmSubmitDisabled}
          submitHandler={submitHandler}
          isSubmittingEndorsement={isSubmittingEndorsement}
        />
      </DialogActions>
    </Dialog>
  );
}

BondEndorsementDialog.propTypes = bondEndorsementDialogPropTypes;
